import {
  EditorContent
} from './ItemEditorPopup.styled';
import {
  MetricsWrapper,
  MetricsContent,
  MetricsWidget,
  MetricsTitle,
  MetricSingle,
  MetricMedium,
  MetricsWidgetContent
} from './Metrics.styled';
import { useTokenAuth } from '../../hooks';
import { getMetrics } from '../../providers/BasejumpApiProvider';
import { AppContext } from '../../providers/AppContext';
import { useEffect, useState, useContext } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { theme } from '../../theme';

const MauChart = ({ mau }) => {
  return (
    <MetricsWidget>
      <MetricsTitle>
        Monthly Active Users
      </MetricsTitle>
      <MetricsWidgetContent>
        <ResponsiveContainer width="100%" aspect={2}>
          <LineChart
              data={mau}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.lightBlue}/>
              <XAxis dataKey="month" stroke="white"/>
              <YAxis stroke="white"/>
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke={theme.selection} activeDot={{ r: 8 }} name="Users" />
            </LineChart>
        </ResponsiveContainer>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const DauChart = ({ dau }) => {
  return (
    <MetricsWidget>
      <MetricsTitle>
        Daily Active Users
      </MetricsTitle>
      <MetricsWidgetContent>
        <ResponsiveContainer width="100%" aspect={2}>
          <LineChart
            data={dau}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.lightBlue}/>
            <XAxis dataKey="date" stroke="white"/>
            <YAxis stroke="white"/>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke={theme.selection} activeDot={{ r: 8 }} name="Users" />
          </LineChart>
        </ResponsiveContainer>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const CcuChart = ({ ccu }) => {
  ccu.forEach(c => {
    c.time = c.timestamp.split('T')[1].split('.')[0];
  });
  return (
    <MetricsWidget>
      <MetricsTitle>
        Concurrent Users Today
      </MetricsTitle>
      <MetricsWidgetContent>
        <ResponsiveContainer width="100%" aspect={2}>
          <LineChart
            data={ccu}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.lightBlue}/>
            <XAxis dataKey="time" stroke="white" tick={{ angle: 0, textAnchor: 'end', dy: 10 }}/>
            <YAxis stroke="white"/>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="ccu" stroke={theme.selection} activeDot={{ r: 8 }} name="CCU" />
          </LineChart>
        </ResponsiveContainer>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const MonthlyCcuChart = ({ ccu }) => {
  ccu.forEach(c => {
    c.label = c.date.split('T')[0];
  });
  return (
    <MetricsWidget>
      <MetricsTitle>
        Daily Peak Concurrent Users (30 days)
      </MetricsTitle>
      <MetricsWidgetContent>
        <ResponsiveContainer width="100%" aspect={2}>
          <LineChart
            data={ccu}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.lightBlue}/>
            <XAxis dataKey="label" stroke="white"/>
            <YAxis stroke="white"/>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="peak_ccu" stroke={theme.selection} activeDot={{ r: 8 }} name="CCU" />
          </LineChart>
        </ResponsiveContainer>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const RetentionChart = ({ retention }) => {
  const retentionData = [
    { percentage: Math.floor(retention.seven_day_retention / retention.total_users * 100), name: '7 Days' },
    { percentage: Math.floor(retention.fourteen_day_retention / retention.total_users * 100), name: '14 Days' },
    { percentage: Math.floor(retention.twenty_one_day_retention / retention.total_users * 100), name: '21 Days' },
    { percentage: Math.floor(retention.thirty_day_retention / retention.total_users * 100), name: '30 Days' },
  ];
  return (
    <MetricsWidget>
      <MetricsTitle>
        % Retention of Users
      </MetricsTitle>
      <MetricsWidgetContent>
        <ResponsiveContainer width="100%" aspect={2}>
          <BarChart
            data={retentionData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.lightBlue}/>
            <XAxis dataKey="name" stroke="white"/>
            <YAxis stroke="white"/>
            <Tooltip />
            <Legend />
            <Bar dataKey="percentage" fill={theme.baseGreen} name="Retention Percentage" />
          </BarChart>
        </ResponsiveContainer>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const TotalAccounts = ({ accountsNumber }) => {
  return (
    <MetricsWidget>
      <MetricsTitle>
        Total Accounts Created
      </MetricsTitle>
      <MetricsWidgetContent>
        <MetricSingle>{accountsNumber}</MetricSingle>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const MaxCcu = ({ maxCcu }) => {
  return (
    <MetricsWidget>
      <MetricsTitle>
        Peak Concurrent Users
      </MetricsTitle>
      <MetricsWidgetContent>
        <MetricSingle>{maxCcu}</MetricSingle>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

const TotalRevenue = ({ revenueLabel }) => {
  return (
    <MetricsWidget>
      <MetricsTitle>
        Total Revenue
      </MetricsTitle>
      <MetricsWidgetContent>
        <MetricMedium>{revenueLabel}</MetricMedium>
      </MetricsWidgetContent>
    </MetricsWidget>
  );
};

export const Metrics = () => {
  const { setErrorData } = useContext(AppContext);
  const [metrics, setMetrics] = useState(null);
  const { basejumpToken } = useTokenAuth();

  const fetchMetrics = async () => {
    try {
      const apiMetrics = await getMetrics(basejumpToken);
      setMetrics(apiMetrics);
    } catch (err) {
      setErrorData({ title: 'Error', description: err.message });
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const maxCcu = metrics?.max_ccu || 0;
  const revenueLabel = `$${((metrics?.total_revenue || 0) / 100).toFixed(2)}`;
  return (
    <EditorContent>
      <MetricsWrapper>
        <MetricsContent>
          {metrics?.retention && <TotalAccounts accountsNumber={metrics.retention.total_users}/>}
          {metrics?.mau && <MauChart mau={metrics.mau}/>}
          {metrics?.dau && <DauChart dau={metrics.dau}/>}
          {metrics?.retention && <RetentionChart retention={metrics.retention}/>}
          {metrics?.ccu && <CcuChart ccu={metrics.ccu}/>}
          {metrics?.m_peak_ccu && <MonthlyCcuChart ccu={metrics.m_peak_ccu}/>}
          <MaxCcu maxCcu={maxCcu}/>
          <TotalRevenue revenueLabel={revenueLabel}/>
        </MetricsContent>
      </MetricsWrapper>
    </EditorContent>
  );
};
